<template>
    <card-tab-panel
        v-if="hasProjects"
        name="project"
        v-bind="$attrs"
        with-variable-height
    >
        <flex-container>
            <input-group v-model="formData.newBuildType">
                <template #label>
                    <input-label>Soort bouw</input-label>
                </template>
                <template #input>
                    <select-input
                        label-key-name="label"
                        value-key-name="value"
                        :disabled="!props.editable"
                        :items="BuildType.values"
                    />
                </template>
            </input-group>

            <template v-if="hasProjects">
                <alert-block
                    v-if="availableProjects.length === 0"
                    :color="Color.blue500"
                    style="width: 100%"
                >
                    <template #prefix>
                        <svg-icon :icon="IconInfo" />
                    </template>

                    <template #default>
                        <div>Geen actieve projecten. Zet een project op actief bij Instellingen &rarr; Projecten om het hier te kunnen kiezen.</div>
                    </template>
                </alert-block>

                <template v-if="isInterCompany">
                    <template v-if="fromReferringCompany">
                        <input-group v-model="formData.project">
                            <template #label>
                                <input-label>Projectnaam</input-label>
                            </template>
                            <template #input>
                                <select-input
                                    label-key-name="name"
                                    value-key-name="id"
                                    :disabled="!props.editable"
                                    :items="availableProjects"
                                />
                            </template>
                        </input-group>
                        <input-group v-model="formData.projectConstructionNumber">
                            <template #label>
                                <input-label>Bouwnummer</input-label>
                            </template>
                            <template #input>
                                <text-input :disabled="!props.editable" />
                            </template>
                        </input-group>
                    </template>

                    <template v-if="fromAssigneeCompany">
                        <alert-block
                            v-if="subject.project"
                            :color="Color.blue500"
                            style="width: 100%"
                        >
                            <template #prefix>
                                <svg-icon :icon="IconInfo" />
                            </template>

                            <template #default>
                                <div>
                                    <span>{{ props.lead.referrer.company.name }}</span>
                                    <span> heeft aangegeven dat het om project </span>
                                    <span v-if="subject.project">{{ subject.project.name }}</span>
                                    <span v-if="subject.projectConstructionNumber"> met bouwnummer {{ subject.projectConstructionNumber }} gaat.</span>
                                </div>
                            </template>
                        </alert-block>

                        <input-group v-model="formData.assigneeProject">
                            <template #label>
                                <input-label>Projectnaam</input-label>
                            </template>
                            <template #input>
                                <select-input
                                    label-key-name="name"
                                    value-key-name="id"
                                    :disabled="!props.editable"
                                    :items="availableProjects"
                                />
                            </template>
                        </input-group>
                        <input-group v-model="formData.assigneeProjectConstructionNumber">
                            <template #label>
                                <input-label>Bouwnummer</input-label>
                            </template>
                            <template #input>
                                <text-input :disabled="!props.editable" />
                            </template>
                        </input-group>
                    </template>
                </template>

                <template v-else>
                    <input-group v-model="formData.project">
                        <template #label>
                            <input-label>Projectnaam</input-label>
                        </template>
                        <template #input>
                            <select-input
                                label-key-name="name"
                                value-key-name="id"
                                :disabled="!props.editable"
                                :items="availableProjects"
                            />
                        </template>
                    </input-group>
                    <input-group v-model="formData.projectConstructionNumber">
                        <template #label>
                            <input-label>Bouwnummer</input-label>
                        </template>
                        <template #input>
                            <text-input :disabled="!props.editable" />
                        </template>
                    </input-group>
                </template>
            </template>
        </flex-container>

        <flex-container
            v-if="props.editable"
            :gap="Size.sm"
            justify-content="flex-end"
            style="margin-top: 20px"
        >
            <button-outline @click="onCancel">
                Annuleren
            </button-outline>

            <button-block
                :disabled="!hasChanges"
                @loading="onSave"
            >
                Opslaan
            </button-block>
        </flex-container>
    </card-tab-panel>
</template>

<script setup lang="ts">
    import {
        ButtonBlock,
        ButtonOutline,
        CardTabPanel,
        FlexContainer,
        Color,
        Size,
    } from '@keyvue/one-ui';

    import { computed, ref, watch } from 'vue';
    import { BuildType, type Lead, Project } from '@keyvue/one-api-client';
    import { ReferralAPI, createResourceIdentifier } from '@keyvue/one-api-client-legacy';
    import useClient, { type ReactiveCollection } from '@composables/useClient';
    import IconInfo from '@keyvue/one-material-icons/icons/info.svg?component';
    import useNotify from '@composables/useNotify';

    export interface Props {
        lead: Lead
        editable?: boolean
        projectsCollection: ReactiveCollection<Project>
    }

    const props = defineProps<Props>();
    const { currentCompany } = useClient();
    const { notifySaving, notifySaved, notifyError } = useNotify();

    const subject = computed(() => props.lead.subject);

    /**
     * Deactivated projects are left out entirely; they are reactivated in the projects settings
     * when needed. The project the lead is currently linked to is always kept in the list,
     * otherwise its selection would look empty.
     */
    const availableProjects = computed(() => {
        const projects = props.projectsCollection.data.value.filter((project) => project.active);

        // ref() unwrapping widens the Project class into a structural type, hence the cast.
        const selected = [formData.value.project, formData.value.assigneeProject] as (Project | null)[];

        selected.forEach((project) => {
            if (project !== null && !projects.some((item) => item.id.equals(project.id))) {
                projects.unshift(project);
            }
        });

        return projects;
    });

    const hasProjects = computed(() => props.projectsCollection.count.value > 0);
    const isInterCompany = computed(() => !props.lead.referrer.company.equals(props.lead.assignee.company));
    const fromReferringCompany = computed(() => currentCompany.value.equals(props.lead.referrer.company));
    const fromAssigneeCompany = computed(() => currentCompany.value.equals(props.lead.assignee.company));

    const formData = ref({
        newBuildType: subject.value.buildType,
        project: subject.value.project,
        projectConstructionNumber: subject.value.projectConstructionNumber,
        assigneeProject: subject.value.assigneeProject,
        assigneeProjectConstructionNumber: subject.value.assigneeProjectConstructionNumber,
    });

    const originalFormData = ref({ ...formData.value });
    const hasChanges = computed(() => Object.keys(formData.value).some((key) => formData.value[key] !== originalFormData.value[key]));
    const resetForm = () => {
        formData.value = { ...originalFormData.value };
    };

    const onSave = async (event: any) => {
        const closeNotification = notifySaving(undefined, 0);

        try {
            const legacyReferral = await ReferralAPI.get(props.lead.id.toString());
            const data = new Map<string, any>([
                ['newBuildType', formData.value.newBuildType.value],
                ['buildNumber', formData.value.projectConstructionNumber],
                ['assigneeBuildNumber', formData.value.assigneeProjectConstructionNumber],
            ]);

            if (formData.value.project) {
                data.set('project', createResourceIdentifier('projects', formData.value.project.id.toString()));
            }

            if (formData.value.assigneeProject) {
                data.set('assigneeProject', createResourceIdentifier('projects', formData.value.assigneeProject.id.toString()));
            }

            await ReferralAPI.update(props.lead.id.toString(), {
                ...legacyReferral,
                ...Object.fromEntries(data),
            } as any);
            notifySaved();
            originalFormData.value = { ...formData.value };
        } catch (error) {
            notifyError(error);
        } finally {
            if (closeNotification) { // @todo: fixed in ai epic branch, if check can be removed after
                closeNotification();
            }

            event.button.setState('completed');
        }
    };

    const onCancel = async () => {
        resetForm();
    };

    watch(() => props.lead.transaction, () => {
        resetForm();
    });
</script>

<style scoped lang="scss">
.financial-tab-panel__fields {
    width: 100%;
    line-height: 125%;
}
</style>
